import { z } from 'zod'
import { PhotoOwnerType, PhotoTag } from '../enums'
import { ALLOWED_IMAGE_TYPES } from './uploads'
import { Id, Timestamp } from './common'

export const PhotoState = z.enum(['pending', 'ready', 'failed'])
export type PhotoState = z.infer<typeof PhotoState>

/**
 * URLs are short-lived signed links (the bucket is private). Location (from
 * EXIF) is returned to the owner only and never leaves in exports.
 */
/** A frame number read from a 'serial' photo: a suggestion the rider confirms. */
export const OcrCandidate = z.object({ text: z.string(), confidence: z.number() })
export type OcrCandidate = z.infer<typeof OcrCandidate>

export const Photo = z.object({
  id: Id,
  owner_type: PhotoOwnerType,
  owner_id: Id,
  tag: PhotoTag,
  state: PhotoState,
  taken_at: Timestamp.nullable(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  urls: z.object({
    original: z.string(),
    display: z.string().nullable(),
    thumb: z.string().nullable(),
  }),
  ocr_state: z.enum(['none', 'pending', 'done', 'unavailable']),
  ocr_candidates: z.array(OcrCandidate),
  created_at: Timestamp,
})
export type Photo = z.infer<typeof Photo>

export const PhotoList = z.object({ photos: z.array(Photo) })
export type PhotoList = z.infer<typeof PhotoList>

export const CreatePhotoBody = z.object({
  object_key: z.string().min(1).max(300),
  content_type: z.enum(ALLOWED_IMAGE_TYPES),
  owner_type: PhotoOwnerType,
  owner_id: Id,
  tag: PhotoTag.default('general'),
  taken_at: Timestamp.optional(),
})
export type CreatePhotoBody = z.input<typeof CreatePhotoBody>

export const UpdatePhotoBody = z.object({ tag: PhotoTag })
export type UpdatePhotoBody = z.infer<typeof UpdatePhotoBody>

export const PhotoListQuery = z.object({ owner_type: PhotoOwnerType, owner_id: Id })

export const SetHeroBody = z.object({ photo_id: Id.nullable() })

export const StorageUsage = z.object({ bytes: z.number(), photos: z.number().int() })
